import { useEffect, useState } from 'react'
import IdentityIcon from '@mui/icons-material/Fingerprint'
import DateRangeIcon from '@mui/icons-material/DateRange'
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined'
import CheckListIcon from '@mui/icons-material/FormatListBulleted'
import { BirthdayToAgeInYearsAndMonths } from 'core/Utilities'
import { Avatar, Box, Button, List, ListItem, Stack, Typography } from '@mui/material'
import S3Avatar from 'Components/S3Image/S3Avatar'
import { sendGet } from 'hooks/use-fetch'
import { type lookup } from 'core/types'

export interface ChildInfoDetails {
  firstName: string
  middleName: string
  lastName: string
  nickName: string | null
  suffix: string | null
  dateOfBirth: Date | null
  dueDate: Date | null
  isUnborn: boolean
  birthGender: string
  uic: string | null
  imageFile: string | null
  childRaceList: string[]
  tribalAffiliationStatus: boolean
  tribalAffiliationName: string | null
  languageID: string
  authorizationStatus: string | null
  eligibilityFactorList: string[]
  environmentalRiskList: string[]
  captureIEP: boolean
  captureIFSP: boolean
  captureEOIS: boolean
  hasIEP: boolean
  hasIFSP: boolean
  hasEOIS: boolean
  hasSNAPBenefits: boolean
  isHomeless: boolean
  isFosterChild: boolean
  isOnCashAssistance: boolean
  isOnSSI: boolean
  isImmigrant: boolean
  readOnly: boolean
  hideShowMoreButton?: boolean | undefined
  pendingDuplicateID: number | null
}

export function ChildInfoLayout (props: ChildInfoDetails): JSX.Element {
  const [showMoreDetails, setShowMoreDetails] = useState(false)
  const [language, setLanguage] = useState<string>('')

  useEffect(() => {
    const fetchLanguages = async (): Promise<void> => {
      const { response: rsp, error: err } = await sendGet('/Language/GetLookup?sort=true', {})
      if (err[0] === '') {
        const selectedLanguage = rsp.find((lang: lookup) => lang.id.toString() === props.languageID.toString())
        setLanguage(selectedLanguage != null ? selectedLanguage.name : 'English')
      }
    }

    if (props.languageID != null) {
      void fetchLanguages()
    } else {
      setLanguage('English')
    }
  }, [props.languageID])

  const toggleMoreDetails = (): void => {
    setShowMoreDetails(!showMoreDetails)
  }
  const raceArray = []
  if (props.childRaceList.length > 0) {
    raceArray.push(...props.childRaceList ?? null)
  }
  if (props.tribalAffiliationStatus) {
    raceArray.push(props.tribalAffiliationName)
  }
  return <Stack direction='column' spacing={1}>
    <Stack direction='row' spacing={1}>
      {props.imageFile !== null && props.imageFile !== ''
        ? <S3Avatar imageKey={props.imageFile} height='30px' width='30px' />
        : <Avatar sx={{ height: '30px', width: '30px' }} />}
      <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
        <Typography variant='body1' sx={{ fontWeight: 'bold', overflowWrap: 'break-word' }} data-testid='fullNameTextDisplay'>
          {props.middleName !== null && props.middleName !== '' ? `${props.firstName} ${props.middleName} ${props.lastName} ${props.suffix ?? ''}` : `${props.firstName} ${props.lastName} ${props.suffix ?? ''}`}
        </Typography>
        {props.nickName !== null && props.nickName !== '' &&
          <Typography variant='body2' sx={{ overflowWrap: 'break-word' }} data-testid='nickNameTextDisplay'>({props.nickName})</Typography>
        }
      </Stack>
    </Stack>
    {props.uic !== null && props.uic !== '' && <Stack direction='row' spacing={1}>
      <IdentityIcon />
      <Typography variant='body1' sx={{ overflowWrap: 'break-word' }} data-testid='uicTextDisplay'>
        {props.uic}
      </Typography>
    </Stack>}

    {props.isUnborn && props.dueDate != null && props.dueDate !== new Date('01-01-0001') && (
      <Stack direction='row' spacing={1}>
        <DateRangeIcon />
        <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }} data-testid="dateOfBirthTextDisplay">
            {new Date(props.dueDate).toLocaleString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Typography>
          {props !== null && <Typography variant='body2' sx={{ overflowWrap: 'break-word' }}>
            ({BirthdayToAgeInYearsAndMonths(props.dueDate, true)})
          </Typography>}
        </Stack>
      </Stack>
    )}
    {!props.isUnborn && props.dateOfBirth != null && props.dateOfBirth !== new Date('01-01-0001') && (
      <Stack direction='row' spacing={1}>
        <DateRangeIcon />
        <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }} data-testid="dateOfBirthTextDisplay">
            {new Date(props?.dateOfBirth).toLocaleString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Typography>
          {props !== null && <Typography variant='body2' sx={{ overflowWrap: 'break-word' }}>
            ({BirthdayToAgeInYearsAndMonths(props.dateOfBirth, false)})
          </Typography>}
        </Stack>
      </Stack>
    )}
    <Stack direction='row' spacing={1}>
      <BallotOutlinedIcon />
      <Stack direction='column'>
        <Typography variant='body1' data-testid='birthGenderTextDisplay' sx={{ overflowWrap: 'break-word' }}>
          {props.birthGender === 'M' ? 'Male' : props.birthGender === 'F' ? 'Female' : ''}
        </Typography>
        <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
          {raceArray?.join(' / ')}
        </Typography>
        <Typography variant='body1' data-testid='primaryLanguageTextDisplay' sx={{ overflowWrap: 'break-word' }}>
          Speaks {language}
        </Typography>
      </Stack>
    </Stack>
    {showMoreDetails && <Stack direction='row' spacing={1}>
      <CheckListIcon />
      <Stack direction='column' spacing={1}>
        <Stack direction='column'>
          <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Authorization</Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
            {props.authorizationStatus ?? '(not entered)'}
          </Typography>
        </Stack>
        {props.eligibilityFactorList.length > 0 && <Stack direction='column'>
          <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Eligibility Factors</Typography>
          <List sx={{ listStyleType: 'disc', paddingLeft: 3, py: 0 }}>
            {props.eligibilityFactorList.map((factor) => (
              <ListItem key={factor} sx={{ display: 'list-item', p: 0 }}>
                <Typography variant='body1' sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {factor}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Stack>}
        {props.environmentalRiskList.length > 0 && <Stack direction='column'>
          <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Environmental Risks</Typography>
          <List sx={{ listStyleType: 'disc', paddingLeft: 3, py: 0 }}>
            {props.environmentalRiskList.map((risk) => (
              <ListItem key={risk} sx={{ display: 'list-item', p: 0 }}>
                <Typography variant='body1' sx={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {risk}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Stack>}
        <Stack direction='column'>
          <Typography variant='body1' sx={{ fontWeight: 'bold' }}>Additional Information</Typography>
          {!props.hasIEP && !props.hasIFSP && !props.hasEOIS && !props.isHomeless && !props.isFosterChild && !props.isOnCashAssistance && !props.isOnSSI && !props.isImmigrant && !props.hasSNAPBenefits
            ? <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>(none)</Typography>
            : <List sx={{ listStyleType: 'disc', paddingLeft: 3, py: 0 }}>
              {props.captureIEP && props.hasIEP && <ListItem sx={{ display: 'list-item', p: 0 }}>Has an IEP</ListItem>}
              {props.captureIFSP && props.hasIFSP && <ListItem sx={{ display: 'list-item', p: 0 }}>Has an IFSP</ListItem>}
              {props.captureEOIS && props.hasEOIS && <ListItem sx={{ display: 'list-item', p: 0 }}>Has an EOIS</ListItem>}
              {props.hasSNAPBenefits && <ListItem sx={{ display: 'list-item', p: 0 }}>Has SNAP Benefits</ListItem>}
              {props.isOnCashAssistance && <ListItem sx={{ display: 'list-item', p: 0 }}>Cash Assistance</ListItem>}
              {props.isFosterChild && <ListItem sx={{ display: 'list-item', p: 0 }}>Foster Child</ListItem>}
              {props.isOnSSI && <ListItem sx={{ display: 'list-item', p: 0 }}>SSI (Supplemental Security Income)</ListItem>}
              {props.isImmigrant && <ListItem sx={{ display: 'list-item', p: 0 }}>Immigrant</ListItem>}
            </List>
          }
        </Stack>
      </Stack>
    </Stack>}
    {props.hideShowMoreButton !== true && (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button data-testid='detail-toggle' sx={{ textTransform: 'none', textDecoration: 'underline', color: 'black', px: 4 }} onClick={toggleMoreDetails}>{showMoreDetails ? 'Hide Details' : 'Show More Details'}</Button>
      </Box>
    )}

  </Stack>
}
